import { ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import lodashSet from 'lodash/set';
import lodashGet from 'lodash/get';
import { Schema as YupSchema, ValidationError as YupValidationError } from 'yup';
import { ZodError, ZodIssue, ZodType } from 'zod';

// https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore#_get
function get(obj: any, path: string, defaultValue?: any) {
  const result = String.prototype.split
    .call(path, /[,[\].]+?/)
    .filter(Boolean)
    .reduce((res: any, key: any) => (res !== null && res !== undefined ? res[key] : res), obj);
  return result === undefined || result === obj ? defaultValue : result;
}

// https://stackoverflow.com/questions/54733539/javascript-implementation-of-lodash-set-method
function set(obj: any, path: any, value: any) {
  if (Object(obj) !== obj) return obj; // When obj is not an object
  // If not yet an array, get the keys from the string-path
  if (!Array.isArray(path)) path = path.toString().match(/[^.[\]]+/g) || [];
  path.slice(0, -1).reduce(
    (
      a: any,
      c: any,
      i: number, // Iterate all of them except the last one
    ) =>
      Object(a[c]) === a[c] // Does the key exist and is its value an object?
        ? // Yes: then follow that path
          a[c]
        : // No: create the key. Is the next key a potential array-index?
          (a[c] =
            Math.abs(path[i + 1]) >> 0 === +path[i + 1]
              ? [] // Yes: assign a new array object
              : {}), // No: assign a new plain object
    obj,
  )[path[path.length - 1]] = value; // Finally assign the value to the last key
  return obj; // Return the top-level object to allow chaining
}

export type Translator = (errorObj: YupValidationError) => string | ReactNode;
export type ZodTranslator = (errorObj: ZodIssue) => string | ReactNode;

export interface ValidationError {
  [key: string]: ValidationError | string;
}

export interface ZodValidationError {
  [key: string]: ZodError | string[];
}

function normalizeValidationError(
  err: YupValidationError,
  translator?: Translator,
): ValidationError {
  const errors = err.inner.reduce((acc, innerError) => {
    const { path, message } = innerError;
    const el: ReturnType<Translator> = translator ? translator(innerError) : message;

    if (!path) {
      return acc;
    }

    // eslint-disable-next-line no-prototype-builtins
    if (acc.hasOwnProperty(path)) {
      const prev = get(acc, path);
      prev.push(el);
      set(acc, path, prev);
    } else {
      set(acc, path, [el]);
    }
    return acc;
  }, {} as ValidationError);

  if (Object.keys(errors).length === 0 && err.path) {
    const el = translator ? translator(err) : err.message;
    set(errors, err.path, [el]);
  }

  return errors;
}

function normalizeZodValidationError(
  err: ZodError,
  translator?: ZodTranslator,
): ZodValidationError {
  return err.issues.reduce((errors, innerError) => {
    const { path, message } = innerError;
    const extendedPath = path
      .map((item) => (typeof item === 'number' ? `[${item}]` : item))
      .join('.');
    const el: ReturnType<ZodTranslator> = translator ? translator(innerError) : message;

    if (path === undefined) {
      return errors;
    }

    // eslint-disable-next-line no-prototype-builtins
    if (errors.hasOwnProperty(extendedPath)) {
      const prev = lodashGet(errors, path);
      prev.push(el);
      lodashSet(errors, extendedPath, prev);
    } else {
      lodashSet(errors, extendedPath, [el]);
    }
    return errors;
  }, {});
}
function logError(err: unknown) {
  console.error(err);
  Sentry.captureException(err);
}

const UNEXPECTED_ERROR_MESSAGE = 'Unexpected validation error';
/**
 * Wraps the execution of a Yup schema to return an Promise<ValidationError>
 * where the key is the form field and the value is the error string.
 */
export function makeValidate<T>(validator: YupSchema<T>, translator?: Translator) {
  return async (values: T): Promise<ValidationError> => {
    try {
      await validator.validate(values, { abortEarly: false, context: { values } });
      return {};
    } catch (err) {
      if (err instanceof YupValidationError) {
        return normalizeValidationError(err, translator);
      }
      // Need log the error otherwise the final-form will intercept it and say nothing
      logError(err);
      return { _global: UNEXPECTED_ERROR_MESSAGE };
    }
  };
}

export function makeZodValidate<T>(validator: ZodType<T>, translator?: ZodTranslator) {
  return async (values: T): Promise<ZodValidationError> => {
    try {
      await validator.parseAsync(values);
      return {};
    } catch (err) {
      if (err instanceof ZodError) {
        return normalizeZodValidationError(err, translator);
      }
      // Need log the error otherwise the final-form will intercept it and say nothing
      logError(err);
      return { _global: [UNEXPECTED_ERROR_MESSAGE] };
    }
  };
}

/**
 * Wraps the sync execution of a Yup schema to return an ValidationError
 * where the key is the form field and the value is the error string.
 */
export function makeValidateSync<T>(validator: YupSchema<T>, translator?: Translator) {
  return (values: T): ValidationError => {
    try {
      validator.validateSync(values, { abortEarly: false });
      return {};
    } catch (err) {
      // We do not care about the type of the error here
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return normalizeValidationError(err, translator);
    }
  };
}
